import { useCallback, useState } from "react";
import { AppTopbar } from "@/components/AppTopbar";
import { ContentSection } from "@/components/ContentSection";
import { useCourseIndex, type CourseSummary } from "@/lib/course";
import { CourseCardForSlug, type CourseStatus } from "@/components/CourseCard";

type StatusFilter = "all" | CourseStatus;

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "in-progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "not-started", label: "Not started" }
];

export default function Courses(): JSX.Element {
  const index = useCourseIndex();

  return (
    <>
      <AppTopbar />
      <main className="p-4 sm:p-8 max-w-5xl mx-auto">
        <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mb-6">Courses</h1>

        <ContentSection id="courses-intro" />

        {index.status === "loading" && <p className="text-muted-foreground">Loading courses…</p>}
        {index.status === "error" && (
          <p className="text-destructive">Failed to load courses: {index.message}</p>
        )}
        {index.status === "ready" && index.courses.length === 0 && (
          <p className="text-muted-foreground">No courses available yet.</p>
        )}
        {index.status === "ready" && index.courses.length > 0 && (
          <Catalog courses={index.courses} />
        )}
      </main>
    </>
  );
}

function Catalog({ courses }: { courses: CourseSummary[] }): JSX.Element {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [statuses, setStatuses] = useState<Record<string, CourseStatus>>({});

  const onStatusResolved = useCallback((slug: string, status: CourseStatus) => {
    setStatuses((prev) => (prev[slug] === status ? prev : { ...prev, [slug]: status }));
  }, []);

  const q = query.trim().toLowerCase();
  const textMatched = courses.filter((c) => c.title.toLowerCase().includes(q));
  const matchedSlugs = textMatched.map((c) => c.slug);

  // Status filter: when not "all", only show cards whose resolved status matches.
  const showWhen = filter === "all" ? undefined : (s: CourseStatus) => s === filter;

  // Empty-state messaging. For "all" we know the count synchronously; for a
  // status filter we wait until every matched card has reported its status.
  const allResolved = matchedSlugs.length > 0 && matchedSlugs.every((s) => statuses[s] !== undefined);
  const visibleWithFilter = matchedSlugs.filter((s) => statuses[s] === filter).length;
  const showEmpty =
    matchedSlugs.length === 0 || (filter !== "all" && allResolved && visibleWithFilter === 0);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search courses…"
          aria-label="Search courses"
          className="flex-1 rounded-control border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                filter === f.value
                  ? "border-primary bg-primary/15 text-primary-soft"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {textMatched.map((c) => (
          <CourseCardForSlug
            key={c.slug}
            courseSlug={c.slug}
            onStatusResolved={onStatusResolved}
            {...(showWhen ? { showWhen } : {})}
          />
        ))}
      </div>

      {showEmpty && (
        <p className="text-muted-foreground">No courses match your search and filters.</p>
      )}
    </>
  );
}
